import { mapListingStatusToPropertyStatus } from "@unreserved/constants/property-card/property-card";
import {
  type ListingActivityDto,
  type ListingDetailsDto,
  type ListingDetailsUserDto,
  type LocationDto,
  type PropertyAmenityDto,
  type PropertyAttributesDto,
  type PropertyDocumentDto,
  type PropertyMediaItemDto,
} from "@unreserved/sdk";
import type { PropertyDetailsUI } from "@unreserved/types/property-details/ui-types";
import { propertyDetailsUtils } from "@unreserved/utils";

import { buildAmenitiesFromApi } from "./amenities-registry";

export function transformListingDetailsForUI(
  details: ListingDetailsDto,
  auctionDetails?: ListingActivityDto | null,
): PropertyDetailsUI {
  const {
    id,
    propertyId,
    title,
    description,
    propertyCategory,
    propertyType,
    listingType,
    listingStatus,
    publishedAt,
    allowPrivateInspection,
    openInspections,
    price,
    propertyAttributes,
    propertyAmenities,
    propertyMedia,
    propertyDocuments,
    location,
    propertyAgents,
    propertyOwners,
  } = details;

  const publishedDate = parseListingDate(publishedAt);
  const daysAgo = publishedDate ? calculateDaysAgo(publishedDate) : 0;

  const badges = buildBadgesFromListing(
    listingType,
    listingStatus,
    auctionDetails?.auctionStatus ?? undefined,
  );
  const features = buildFeaturesFromListingAttributes(
    propertyCategory,
    propertyType,
    propertyAttributes,
  );
  const amenities = buildAmenitiesFromListing(propertyAmenities);
  const metadata = buildMetadataFromListing(propertyCategory, propertyType, propertyAttributes);
  const documents = buildDocumentsFromListing(propertyDocuments);
  const agents = buildAgentsFromListing(propertyAgents);
  const owners = buildOwnersFromListing(propertyOwners);
  const inspectionSchedule = buildInspectionScheduleFromListing(
    allowPrivateInspection,
    openInspections,
  );
  const formattedLocation = buildLocationFromListing(location, title);
  const images = buildImagesFromListing(propertyMedia);
  const infoSections = buildInfoSectionsFromListing(listingType);

  return {
    id: propertyId || id,
    address: formattedLocation.address,
    price: typeof price === "number" ? price : 0,
    salePrice: listingType === "auction" ? "Reserve Price" : "Sale Price",
    listedDate: publishedDate ? propertyDetailsUtils.formatListedOnDate(publishedDate) : "",
    daysAgo: formatDaysAgoDisplay(daysAgo),
    description: coerceListingDescription(description),
    images,
    badges,
    metadata,
    features,
    amenities,
    documents,
    location: formattedLocation,
    agents,
    owners,
    inspectionSchedule,
    infoSections,
    title,
  };
}

function buildBadgesFromListing(
  listingType: ListingDetailsDto["listingType"],
  listingStatus: ListingDetailsDto["listingStatus"],
  auctionStatus?: ListingActivityDto["auctionStatus"] | undefined,
): PropertyDetailsUI["badges"] {
  const statusLabel = mapListingStatusToPropertyStatus(
    listingType as "auction" | "private_sale" | "reverse_auction",
    listingStatus as
      | "draft"
      | "active"
      | "under_offer"
      | "off_the_market"
      | "passed_in"
      | "disabled"
      | "deleted"
      | "sold",
    auctionStatus as
      | "to_be_started"
      | "in_progress"
      | "paused"
      | "completed"
      | "cancelled"
      | undefined,
  );

  const rawStatus = auctionStatus ?? listingStatus ?? "";

  return [
    {
      type: "sale-type" as const,
      label: formatListingType(listingType),
      variant: "default" as const,
    },
    {
      type: "status" as const,
      label: statusLabel,
      variant: getStatusVariant(rawStatus),
    },
  ];
}

const HOW_IT_WORKS_CONTENT: Record<string, { title: string; content: string[] }> = {
  auction: {
    title: "How Live Auction Works",
    content: [
      "Register & Verify: Complete ID verification via Stripe Identity and get approved to bid.",
      "Browse & Watch: View property details, track current price, bid history, and countdown timer. Save or watch listings.",
      "Start Bidding: Place bids in real time. All bids are public and timestamped.",
      "Stay in the Auction: Each new bid extends the timer. You'll have time to respond — no last-second surprises.",
      "Win the Property: When the timer ends with no new bids, the highest bidder wins (subject to the reserve price).",
      "Secure the Purchase: If the reserve is met, the final bid is the winner. Sale is confirmed instantly — no further negotiations. System prepares the contract and sends it to you for signing.",
    ],
  },
  reverse_auction: {
    title: "How Reverse Auction Works",
    content: [
      "Register & Verify: Complete ID verification via Stripe Identity and get approved to participate.",
      "Watch the Price: View the starting price (above reserve), track price drops and countdown timing, and set your target price.",
      "Set Alerts (Optional): Nominate the price you're willing to pay and get notified when the listing reaches your number.",
      "Price Drops Over Time: Price reduces at set intervals. All drops are public and timestamped.",
      "Act at Your Price: When the price hits your level, you can buy immediately. No bidding war — just decide and act.",
      "Secure the Property: First buyer to meet or exceed the reserve wins instantly. Sale is confirmed — no negotiation.",
    ],
  },
  private_sale: {
    title: "How Private Sale Works",
    content: [
      "A private sale (also called a private treaty) is when a property is sold through direct negotiation between buyer and vendor — not at auction.",
      "Go to Your Dashboard: Log in to Unreserved and navigate to your Dashboard to see all properties you have enquired on.",
      'Select the Property: Click on the property you want to make an offer on and click the "Make an Offer" button.',
      "Enter Your Offer Details: Fill in your price, settlement terms, settlement date, offer expiry date, residential address, and any conditions (finance, building/pest inspection, subject to sale).",
      "Add Special Conditions (Optional): Include any special conditions such as subject to finance or subject to building and pest inspection.",
      'Submit Your Offer: Click "Submit Offer". The conveyancer will prepare the contract of sale and send it to you for signing.',
      "Receive & Sign Your Contract: You'll be notified via push notification and email when the contract is ready. Follow the prompts to sign digitally.",
      "Pay Your Deposit: Once contracts are signed by both parties, pay your deposit — typically 10% of the purchase price.",
    ],
  },
};

const AUCTION_FAQS = [
  {
    question: "How do I join an auction?",
    answer: "Register on the platform and verify your identity to unlock bidding.",
  },
  { question: "Where do I bid?", answer: "On the property listing via the live auction panel." },
  { question: "When does bidding start?", answer: "When the countdown timer reaches zero." },
  {
    question: "How do I place a bid?",
    answer: "Select an increment or enter an amount, then submit.",
  },
  {
    question: "What happens when someone bids?",
    answer: "The timer resets to give others time to respond.",
  },
  {
    question: "Can I see other bids?",
    answer: "Yes — bid history and current highest bid update live.",
  },
  {
    question: 'What does "Reserve Met" mean?',
    answer: "The seller's minimum price has been reached.",
  },
  { question: "When does the auction end?", answer: "When the timer runs out with no new bids." },
  {
    question: "What if I win?",
    answer: "You'll be prompted to complete your offer and sign the contract.",
  },
  {
    question: "Is everything done online?",
    answer: "Yes — bidding, offers, and contracts are all fully digital.",
  },
];

const PRIVATE_SALE_FAQS = [
  {
    question: "How do I make an offer?",
    answer: 'Go to your dashboard, select a property, and click "Make an Offer."',
  },
  {
    question: "What details do I need to include?",
    answer: "Price, settlement terms, settlement date, expiry date, and your address.",
  },
  {
    question: "Can I add conditions to my offer?",
    answer: "Yes, you can include special conditions if needed.",
  },
  {
    question: "What happens after I submit my offer?",
    answer: "The contract is prepared and sent to you for signing.",
  },
  {
    question: "How will I know when the contract is ready?",
    answer: "You'll receive a notification via email and in the platform.",
  },
  {
    question: "How do I sign the contract?",
    answer: "Digitally — just follow the prompts, no printing required.",
  },
  { question: "Can I track my offer?", answer: "Yes, through your dashboard and notifications." },
  {
    question: "Is my offer legally binding?",
    answer: "It becomes binding once the contract is signed by all parties.",
  },
];

function buildInfoSectionsFromListing(
  listingType: ListingDetailsDto["listingType"],
): PropertyDetailsUI["infoSections"] {
  const isPrivateSale = listingType === "private_sale";
  const howItWorks = HOW_IT_WORKS_CONTENT[listingType] ?? HOW_IT_WORKS_CONTENT.auction;
  const faqTitle = isPrivateSale ? "Private Sale FAQs" : "Auction FAQs";
  const faqItems = isPrivateSale ? PRIVATE_SALE_FAQS : AUCTION_FAQS;

  return [
    {
      id: "info-faqs",
      title: faqTitle,
      icon: "chat",
      iconBgColor: "#f8dfb0",
      faqItems,
    },
    {
      id: "info-how-it-works",
      title: howItWorks.title,
      icon: "video",
      iconBgColor: "#f8dfb0",
      content: howItWorks.content,
    },
    {
      id: "info-terms",
      title: "Terms and Conditions",
      icon: "document",
      iconBgColor: "#f8dfb0",
    },
  ];
}

export function formatListingType(type: string): string {
  const typeMap: Record<string, string> = {
    auction: "Live Auction",
    private_sale: "Private Sale",
    reverse_auction: "Reverse Auction",
  };
  return typeMap[type] || type;
}

function getStatusVariant(status: string): "default" | "success" | "warning" | "info" {
  const variantMap: Record<string, "default" | "success" | "warning" | "info"> = {
    draft: "default",
    scheduled: "info",
    active: "success",
    in_progress: "success",
    to_be_started: "success",
    under_offer: "info",
    paused: "warning",
    ended: "default",
    completed: "default",
    cancelled: "warning",
    passed_in: "default",
    sold: "success",
  };
  return variantMap[status] || "default";
}

function coerceListingDescription(raw: ListingDetailsDto["description"]): string {
  if (raw == null) {
    return "";
  }
  if (typeof raw === "string") {
    return raw;
  }
  try {
    return String(raw);
  } catch {
    return "";
  }
}

function parseListingDate(value: ListingDetailsDto["publishedAt"]): Date | null {
  if (typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function calculateDaysAgo(date: Date): number {
  const diffMs = Date.now() - date.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return days < 0 ? 0 : days;
}

function formatDaysAgoDisplay(daysAgo: number): string {
  return daysAgo === 0 ? "Today" : `${daysAgo} days ago`;
}

function buildMetadataFromListing(
  propertyCategory: ListingDetailsDto["propertyCategory"],
  propertyType: unknown,
  attributes: PropertyAttributesDto,
): PropertyDetailsUI["metadata"] {
  const currentYear = new Date().getFullYear();
  const yearBuilt = propertyDetailsUtils.toNumber(attributes.yearBuilt);
  const age = yearBuilt ? `${currentYear - yearBuilt} years` : "Unknown";

  const propertyTypeLabel =
    typeof propertyType === "string" && propertyType.length > 0 ? propertyType : propertyCategory;

  const status = propertyDetailsUtils.toString(attributes.propertyAge);

  return {
    propertyType: propertyTypeLabel,
    age,
    status: status,
  };
}

function buildAmenitiesFromListing(
  amenities: PropertyAmenityDto[] | undefined,
): PropertyDetailsUI["amenities"] {
  if (!amenities || amenities.length === 0) {
    return [];
  }

  // Reuse existing registry by mapping to its expected shape
  return buildAmenitiesFromApi(
    amenities.map((amenity) => ({
      name: amenity.name,
      icon: amenity.icon != null ? String(amenity.icon) : "",
    })),
  );
}

function buildDocumentsFromListing(
  propertyDocuments: PropertyDocumentDto[] | undefined,
): PropertyDetailsUI["documents"] {
  if (!propertyDocuments || propertyDocuments.length === 0) {
    return [];
  }

  return propertyDocuments.map((doc, index) => {
    const meta = doc.mediaMetadata;
    return {
      id: doc.id,
      title: meta.fileName ?? `Document ${index + 1}`,
      description: propertyDetailsUtils.toString(meta.altText),
      fileSize: undefined,
      icon: "document",
      iconBgColor: "#edf0f5",
      action: "download" as const,
      downloadUrl: meta.fileUrl,
    };
  });
}

function buildAgentsFromListing(
  agents: ListingDetailsUserDto[] | undefined | null,
): PropertyDetailsUI["agents"] {
  if (!agents || agents.length === 0) {
    return [];
  }

  return agents.map((agent) => ({
    id: agent.id,
    name: agent.fullName,
    type: "agent" as const,
    description: "You will be speaking with the agent directly",
    avatarUrl: typeof agent.avatar === "string" ? agent.avatar : undefined,
    isVerified: agent.isVerified,
  }));
}

function buildOwnersFromListing(
  owners: ListingDetailsUserDto[] | undefined | null,
): NonNullable<PropertyDetailsUI["owners"]> {
  if (!owners || owners.length === 0) {
    return [];
  }

  return owners.map((owner) => ({
    id: owner.id,
    name: owner.fullName,
    type: "vendor" as const,
    description: "You will be speaking with the property owner directly",
    avatarUrl: typeof owner.avatar === "string" ? owner.avatar : undefined,
    isVerified: owner.isVerified,
  }));
}

function buildInspectionScheduleFromListing(
  allowPrivateInspection: ListingDetailsDto["allowPrivateInspection"],
  openInspections: ListingDetailsDto["openInspections"],
): PropertyDetailsUI["inspectionSchedule"] {
  const inspections = openInspections ?? [];

  const now = Date.now();
  const parsed = inspections
    .map((inspection) => {
      const start = propertyDetailsUtils.toDate(inspection.inspectionStartTime);
      const end = propertyDetailsUtils.toDate(inspection.inspectionEndTime);

      if (!start || !end) {
        return null;
      }

      return { start, end };
    })
    .filter((value): value is { start: Date; end: Date } => value !== null);

  const upcoming = parsed
    .filter((item) => item.start.getTime() >= now)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const candidates =
    upcoming.length > 0 ? upcoming : parsed.sort((a, b) => a.start.getTime() - b.start.getTime());

  const slots = candidates.map((item) => ({
    startTime: item.start.toISOString(),
    endTime: item.end.toISOString(),
  }));

  return {
    slots,
    privateInspectionAvailable: allowPrivateInspection,
  };
}

function buildLocationFromListing(
  location: LocationDto | undefined,
  fallbackTitle: string,
): PropertyDetailsUI["location"] {
  if (!location) {
    return {
      address: fallbackTitle,
      coordinates: { lat: 0, lng: 0 },
    };
  }

  const displayAddress = propertyDetailsUtils.toString(location.displayAddress);
  const address = displayAddress.trim() || fallbackTitle;

  const latitude = propertyDetailsUtils.toNumber(location.latitude);
  const longitude = propertyDetailsUtils.toNumber(location.longitude);

  return {
    address,
    coordinates: {
      lat: latitude ?? 0,
      lng: longitude ?? 0,
    },
    mapImageUrl: "",
  };
}

function buildImagesFromListing(
  mediaItems: PropertyMediaItemDto[] | undefined,
): PropertyDetailsUI["images"] {
  if (!mediaItems || mediaItems.length === 0) {
    return [];
  }

  return mediaItems.map((item, index) => {
    const meta = item.mediaMetadata;
    return {
      id: item.id,
      url: meta.fileUrl ?? "",
      alt: propertyDetailsUtils.toString(meta.altText) || `Property image ${index + 1}`,
      order: typeof item.displayOrder === "number" ? (item.displayOrder as number) : index + 1,
      type: "image" as const,
      videoUrl: undefined,
      posterUrl: undefined,
    };
  });
}

function buildFeaturesFromListingAttributes(
  propertyCategory: ListingDetailsDto["propertyCategory"],
  propertyType: unknown,
  attributes: PropertyAttributesDto,
): PropertyDetailsUI["features"] {
  const FEATURE_ICON_MAP: Record<string, string> = {
    "feature-land-area": "square",
    "feature-floor-area": "floor",
    "feature-frontage": "square",
    "feature-bedrooms": "bed",
    "feature-bathrooms": "bath",
    "feature-energy-rating": "bulb",
    "feature-parking": "car",
    "feature-office-area": "office",
    "feature-open-parking-spaces": "car",
    "feature-zoning": "map",
  };

  const features: PropertyDetailsUI["features"] = [];

  const categoryFromType =
    typeof propertyType === "string" && propertyType.length > 0 ? propertyType.toLowerCase() : "";
  const categoryFromCategory = (propertyCategory ?? "").toString().toLowerCase();
  const category = categoryFromType || categoryFromCategory;

  const landArea = propertyDetailsUtils.toNumber(attributes.landArea);
  const landAreaUnit = attributes.landAreaUnit ?? "sqm";
  const floorArea = propertyDetailsUtils.toNumber(attributes.floorArea);
  const floorAreaUnit = attributes.floorAreaUnit ?? "sqm";
  const frontage = propertyDetailsUtils.toNumber(attributes.frontage);
  const frontageUnit = attributes.frontageUnit ?? "m";
  const energyRating = propertyDetailsUtils.toNumber(attributes.energyRating);

  const addLandArea = () => {
    if (!landArea) return;
    features.push({
      id: "feature-land-area",
      icon: FEATURE_ICON_MAP["feature-land-area"],
      label: "Land Area",
      value: `${landArea} ${landAreaUnit}`,
    });
  };

  const addFloorArea = () => {
    if (!floorArea) return;
    features.push({
      id: "feature-floor-area",
      icon: FEATURE_ICON_MAP["feature-floor-area"],
      label: "Floor Area",
      value: `${floorArea} ${floorAreaUnit}`,
    });
  };

  const addFrontage = () => {
    if (!frontage) return;
    features.push({
      id: "feature-frontage",
      icon: FEATURE_ICON_MAP["feature-frontage"],
      label: "Frontage",
      value: `${frontage} ${frontageUnit}`,
    });
  };

  const addBedrooms = () => {
    if (!attributes.bedrooms) return;
    features.push({
      id: "feature-bedrooms",
      icon: FEATURE_ICON_MAP["feature-bedrooms"],
      label: "Bedrooms",
      value: propertyDetailsUtils.toString(attributes.bedrooms),
    });
  };

  const addBathrooms = () => {
    if (!attributes.bathrooms) return;
    features.push({
      id: "feature-bathrooms",
      icon: FEATURE_ICON_MAP["feature-bathrooms"],
      label: "Bathrooms",
      value: propertyDetailsUtils.toString(attributes.bathrooms),
    });
  };

  const addEnergyRating = () => {
    if (!energyRating) return;
    features.push({
      id: "feature-energy-rating",
      icon: FEATURE_ICON_MAP["feature-energy-rating"],
      label: "Energy Rating",
      value: energyRating,
    });
  };

  const addOpenParkingSpaces = () => {
    const openParking = propertyDetailsUtils.toNumber(attributes.openParkingSpaces);
    if (!openParking) return;
    features.push({
      id: "feature-open-parking-spaces",
      icon: FEATURE_ICON_MAP["feature-open-parking-spaces"],
      label: "Open Parking Spaces",
      value: propertyDetailsUtils.toString(openParking),
    });
  };

  const addZoning = () => {
    if (!attributes.zoning) return;
    features.push({
      id: "feature-zoning",
      icon: FEATURE_ICON_MAP["feature-zoning"],
      label: "Zoning",
      value: propertyDetailsUtils.toString(attributes.zoning),
    });
  };

  const CATEGORY_FEATURE_BUILDERS: Record<string, Array<() => void>> = {
    residential: [addBedrooms, addBathrooms, addLandArea, addEnergyRating, addFloorArea],
    rural: [addBedrooms, addBathrooms, addLandArea, addOpenParkingSpaces, addEnergyRating],
    land: [addLandArea, addFrontage, addZoning],
    default: [
      addBedrooms,
      addBathrooms,
      addLandArea,
      addEnergyRating,
      addFloorArea,
      addFrontage,
      addZoning,
    ],
  };

  const builders = CATEGORY_FEATURE_BUILDERS[category] ?? CATEGORY_FEATURE_BUILDERS.default;

  builders?.forEach((build) => build());

  return features;
}
